"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "motion/react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * SegmentedControl — a compact single-select toggle: a bordered track holds
 * two or more segments; the selected one is lifted by a "thumb" pill that
 * slides between segments as the value changes.
 *
 * Built from scratch (not Radix) so it can own the animated thumb, but it
 * follows the same house idioms as the rest of the library:
 * - Fully data-driven (`options` / `value` / `onChange`), like `<FilterBar>` /
 *   `<FeatureTabs>` — usable as a generic control, not a one-shot layout.
 * - Roving tabindex + arrow-key navigation with **selection-follows-focus**,
 *   the expected feel for a segmented control (arrow moves AND selects). Home /
 *   End jump to the first / last enabled segment; disabled segments are skipped.
 *   Space / Enter select the focused segment. Only the selected (or, when
 *   nothing is selected, the first enabled) segment is in the Tab order.
 * - Two ARIA shapes for two jobs — `semantics="tablist"` (default; `role=tab`,
 *   `aria-selected`, optional `aria-controls` per option) when the segments
 *   switch a view, and `semantics="radiogroup"` (`role=radio`, `aria-checked`)
 *   when they set a value. Give the group a name via `aria-label` /
 *   `aria-labelledby`.
 * - Reduced-motion aware: the thumb slide is a `motion` layout animation that
 *   degrades to an instant snap under `prefers-reduced-motion` (the thumb still
 *   renders under the selected segment — only the slide is dropped).
 * - Token-driven (`bg-muted` track, `bg-background` thumb, `text-foreground` /
 *   `text-muted-foreground`) so it retints per studio and reads in light/dark.
 *
 *   <SegmentedControl
 *     aria-label="Response mode"
 *     semantics="radiogroup"
 *     defaultValue="fast"
 *     options={[
 *       { value: "fast", label: "Fast" },
 *       { value: "pro",  label: "Pro" },
 *     ]}
 *     onChange={setMode}
 *   />
 */

export interface SegmentedControlOption {
  /** Stable id used as the value + React key. */
  value: string;
  /** Visible label. */
  label: React.ReactNode;
  /** Optional icon node (Lucide / SVG), rendered before the label. */
  icon?: React.ReactNode;
  /** Disable just this segment. Skipped by arrow-key navigation. */
  disabled?: boolean;
  /**
   * `id` of the panel this segment controls. Wired to `aria-controls` only
   * under `semantics="tablist"` (the segment is then a real `role=tab`).
   */
  controls?: string;
  /** Accessible name for the segment — required when `label` is icon-only. */
  "aria-label"?: string;
}

export const segmentedControlVariants = cva(
  "relative isolate inline-flex items-center rounded-full border border-border bg-muted p-1 text-muted-foreground",
  {
    variants: {
      size: {
        sm: "gap-0.5",
        md: "gap-1",
      },
      fullWidth: {
        true: "flex w-full",
        false: "",
      },
    },
    defaultVariants: {
      size: "md",
      fullWidth: false,
    },
  },
);

const SEGMENT_SIZES = {
  sm: "h-7 gap-1.5 px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
  md: "h-9 gap-1.5 px-3.5 text-sm [&_svg:not([class*='size-'])]:size-4",
} as const;

export interface SegmentedControlProps
  extends
    Omit<React.ComponentPropsWithoutRef<"div">, "onChange" | "defaultValue">,
    VariantProps<typeof segmentedControlVariants> {
  /** The segments to render. Order drives keyboard navigation. */
  options: SegmentedControlOption[];
  /** Controlled selected value. Pair with `onChange`. */
  value?: string;
  /** Uncontrolled initial value. Ignored once `value` is provided. */
  defaultValue?: string;
  /** Fires with the newly-selected value (click or keyboard). */
  onChange?: (value: string) => void;
  /**
   * ARIA shape. `"tablist"` (default) when the segments switch a view;
   * `"radiogroup"` when they set a value with no associated panel.
   */
  semantics?: "tablist" | "radiogroup";
  /** Disable the whole control. */
  disabled?: boolean;
  /** Animate the thumb slide. Default `true`; ignored under reduced motion. */
  motion?: boolean;
}

export const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(
  function SegmentedControl(
    {
      options,
      value,
      defaultValue,
      onChange,
      size = "md",
      fullWidth = false,
      semantics = "tablist",
      disabled = false,
      motion: motionProp = true,
      className,
      ...rest
    },
    ref,
  ) {
    const reduced = useReducedMotion();
    // `null` (pre-resolve) counts as motion-allowed to avoid an SSR flash,
    // matching the hook's documented semantics.
    const useMotion = motionProp && reduced !== true;

    // Stable per-instance layoutId so two SegmentedControls on one page don't
    // cross-animate their thumbs.
    const thumbId = React.useId();

    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<string | undefined>(defaultValue);
    const currentValue = isControlled ? value : internalValue;

    // Local refs to each segment button for roving-focus management.
    const segmentRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

    const isTablist = semantics !== "radiogroup";
    const selectedIndex = options.findIndex((o) => o.value === currentValue);
    const firstEnabledIndex = options.findIndex((o) => !o.disabled);
    // Exactly one segment is tabbable (roving tabindex): the selected one, or —
    // when nothing is selected — the first enabled one, so the group is reachable.
    const tabbableIndex = selectedIndex >= 0 ? selectedIndex : firstEnabledIndex;

    const select = React.useCallback(
      (next: string) => {
        if (disabled) return;
        if (!isControlled) setInternalValue(next);
        if (next !== currentValue) onChange?.(next);
      },
      [disabled, isControlled, currentValue, onChange],
    );

    // Move focus to (and select) an enabled segment, stepping over disabled ones.
    const focusIndex = React.useCallback(
      (from: number, dir: 1 | -1) => {
        const n = options.length;
        for (let step = 1; step <= n; step++) {
          const i = (from + dir * step + n * step) % n;
          if (!options[i]?.disabled) {
            segmentRefs.current[i]?.focus();
            select(options[i].value);
            return;
          }
        }
      },
      [options, select],
    );

    const focusEdge = React.useCallback(
      (edge: "first" | "last") => {
        const order =
          edge === "first"
            ? options.map((_, i) => i)
            : options.map((_, i) => options.length - 1 - i);
        for (const i of order) {
          if (!options[i]?.disabled) {
            segmentRefs.current[i]?.focus();
            select(options[i].value);
            return;
          }
        }
      },
      [options, select],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (disabled) return;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          focusIndex(index, 1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          focusIndex(index, -1);
          break;
        case "Home":
          e.preventDefault();
          focusEdge("first");
          break;
        case "End":
          e.preventDefault();
          focusEdge("last");
          break;
        case " ":
        case "Enter":
          // Selection already follows focus, but Space/Enter must not scroll
          // or submit an enclosing form — and they re-assert selection if
          // focus and selection ever diverge.
          e.preventDefault();
          if (!options[index]?.disabled) select(options[index].value);
          break;
        default:
          break;
      }
    };

    return (
      <div
        ref={ref}
        data-slot="segmented-control"
        role={isTablist ? "tablist" : "radiogroup"}
        aria-orientation="horizontal"
        aria-disabled={disabled || undefined}
        className={cn(
          segmentedControlVariants({ size, fullWidth }),
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
        {...rest}
      >
        {options.map((opt, i) => {
          const selected = opt.value === currentValue;
          const segDisabled = disabled || !!opt.disabled;
          return (
            <button
              key={opt.value}
              ref={(el) => {
                segmentRefs.current[i] = el;
              }}
              type="button"
              data-slot="segmented-control-item"
              data-state={selected ? "active" : "inactive"}
              role={isTablist ? "tab" : "radio"}
              aria-selected={isTablist ? selected : undefined}
              aria-checked={isTablist ? undefined : selected}
              aria-controls={isTablist ? opt.controls : undefined}
              aria-label={opt["aria-label"]}
              // Roving tabindex: only the tabbable segment participates in Tab.
              tabIndex={i === tabbableIndex && !segDisabled ? 0 : -1}
              disabled={segDisabled}
              onClick={() => select(opt.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className={cn(
                "relative inline-flex shrink-0 select-none items-center justify-center rounded-full font-medium",
                "cursor-pointer whitespace-nowrap transition-colors duration-180 ease-smooth-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                "disabled:cursor-not-allowed disabled:opacity-50",
                SEGMENT_SIZES[size ?? "md"],
                fullWidth && "flex-1",
                selected ? "text-foreground" : "hover:text-foreground",
              )}
            >
              {selected ? (
                useMotion ? (
                  <motion.span
                    aria-hidden
                    data-slot="segmented-control-thumb"
                    layoutId={`${thumbId}-thumb`}
                    className="absolute inset-0 rounded-full bg-background shadow-sm"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                ) : (
                  <span
                    aria-hidden
                    data-slot="segmented-control-thumb"
                    className="absolute inset-0 rounded-full bg-background shadow-sm"
                  />
                )
              ) : null}
              <span className="relative z-10 inline-flex items-center gap-1.5">
                {opt.icon}
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  },
);

SegmentedControl.displayName = "SegmentedControl";
